import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, FileText, Loader2, Save, X } from 'lucide-react';
import { apiClient } from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import RichTextEditor from '../../components/admin/RichTextEditor';

export default function Pages() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [currentPage, setCurrentPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    slug: '',
    title: '',
    body_rich_json: null,
    excerpt: '',
    seo_title: '',
    seo_description: '',
    status: 'draft',
  });

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/pages/');
      setPages(response);
    } catch (error) {
      console.error('Failed to load pages:', error);
      toast.error('Грешка при зареждане на страниците');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setCurrentPage(null);
    setFormData({
      slug: '',
      title: '',
      body_rich_json: null,
      excerpt: '',
      seo_title: '',
      seo_description: '',
      status: 'draft',
    });
    setShowEditor(true);
  };

  const handleEdit = (page) => {
    setCurrentPage(page);
    setFormData({
      slug: page.slug,
      title: page.title,
      body_rich_json: page.body_rich_json,
      excerpt: page.excerpt || '',
      seo_title: page.seo_title || '',
      seo_description: page.seo_description || '',
      status: page.status,
    });
    setShowEditor(true);
  };

  const handleSave = async (status = null) => {
    try {
      setSaving(true);

      const dataToSave = {
        ...formData,
        status: status || formData.status,
      };

      if (!dataToSave.slug || !dataToSave.title) {
        toast.error('Slug и заглавие са задължителни');
        return;
      }

      let response;
      if (currentPage) {
        // Update existing
        response = await apiClient.patch(`/api/pages/${currentPage._id}`, dataToSave);
      } else {
        // Create new
        response = await apiClient.post('/api/pages/', dataToSave);
      }

      if (response.success) {
        toast.success(currentPage ? 'Страницата е обновена' : 'Страницата е създадена');
        setShowEditor(false);
        loadPages();

        // Create revision if published
        if (status === 'published' || (currentPage && currentPage.status === 'published')) {
          try {
            const pageId = currentPage?._id || response.page._id;
            await apiClient.post('/api/revisions/', {
              entity_type: 'page',
              entity_id: pageId,
              change_note: status === 'published' ? 'Published' : 'Updated',
            });
          } catch (revError) {
            console.error('Failed to create revision:', revError);
          }
        }
      }
    } catch (error) {
      console.error('Save failed:', error);
      toast.error(error.response?.data?.detail || 'Грешка при запазване');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (pageId) => {
    try {
      await apiClient.post(`/api/pages/${pageId}/publish`, {});
      toast.success('Страницата е публикувана');
      loadPages();

      // Create revision
      try {
        await apiClient.post('/api/revisions/', {
          entity_type: 'page',
          entity_id: pageId,
          change_note: 'Published',
        });
      } catch (revError) {
        console.error('Failed to create revision:', revError);
      }
    } catch (error) {
      console.error('Publish failed:', error);
      toast.error('Грешка при публикуване');
    }
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете тази страница?')) {
      return;
    }

    try {
      await apiClient.delete(`/api/pages/${pageId}`);
      toast.success('Страницата е изтрита');
      loadPages();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Грешка при изтриване');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-neutral-700 text-neutral-300',
      published: 'bg-green-900/30 text-green-400',
      archived: 'bg-red-900/30 text-red-400',
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[status] || colors.draft}`}>
        {status === 'draft' ? 'Чернова' : status === 'published' ? 'Публикувана' : 'Архивирана'}
      </span>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-playfair font-bold">Страници</h1>
          <p className="text-neutral-400 mt-1">Управление на съдържание</p>
        </div>
        <Button onClick={handleCreate} className="bg-accent-gold hover:bg-accent-gold-dark">
          <Plus className="w-4 h-4 mr-2" />
          Нова страница
        </Button>
      </div>

      {/* Pages List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-accent-gold" />
        </div>
      ) : pages.length === 0 ? (
        <div className="text-center py-20">
          <FileText className="w-16 h-16 mx-auto mb-4 text-neutral-600" />
          <p className="text-neutral-400">Няма създадени страници</p>
          <Button onClick={handleCreate} variant="link" className="mt-2 text-accent-gold">
            Създайте първата си страница
          </Button>
        </div>
      ) : (
        <div className="bg-primary-charcoal rounded-lg border border-neutral-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-primary-black border-b border-neutral-700">
              <tr>
                <th className="text-left px-6 py-3 text-sm font-medium text-neutral-300">Заглавие</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-neutral-300">Slug</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-neutral-300">Статус</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-neutral-300">Обновена</th>
                <th className="text-right px-6 py-3 text-sm font-medium text-neutral-300">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-700">
              {pages.map((page) => (
                <tr key={page._id} className="hover:bg-primary-black/50 transition-colors">
                  <td className="px-6 py-4 font-medium">{page.title}</td>
                  <td className="px-6 py-4 text-sm text-neutral-400 font-mono">/{page.slug}</td>
                  <td className="px-6 py-4">{getStatusBadge(page.status)}</td>
                  <td className="px-6 py-4 text-sm text-neutral-400">
                    {new Date(page.updated_at).toLocaleDateString('bg-BG')}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {page.status === 'published' && (
                        <a
                          href={`/p/${page.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-neutral-700 rounded transition-colors"
                          title="Преглед"
                        >
                          <Eye className="w-4 h-4" />
                        </a>
                      )}
                      <button
                        onClick={() => handleEdit(page)}
                        className="p-2 hover:bg-neutral-700 rounded transition-colors"
                        title="Редактиране"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {page.status === 'draft' && (
                        <button
                          onClick={() => handlePublish(page._id)}
                          className="px-3 py-1 bg-green-900/30 text-green-400 text-sm rounded hover:bg-green-900/50 transition-colors"
                        >
                          Публикувай
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(page._id)}
                        className="p-2 hover:bg-red-900/30 text-red-400 rounded transition-colors"
                        title="Изтриване"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Page Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="bg-primary-charcoal border-neutral-700 max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{currentPage ? 'Редактиране на страница' : 'Нова страница'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Заглавие *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Моята страница"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Slug *</label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  placeholder="moia-stranitsa"
                />
              </div>
            </div>

            {/* Rich Text Editor */}
            <div>
              <label className="block text-sm font-medium mb-2">Съдържание</label>
              <RichTextEditor
                content={formData.body_rich_json}
                onChange={(json) => setFormData({ ...formData, body_rich_json: json })}
              />
            </div>

            {/* SEO */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium border-b border-neutral-700 pb-2">SEO</h3>
              <div>
                <label className="block text-sm font-medium mb-2">SEO Заглавие</label>
                <Input
                  value={formData.seo_title}
                  onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                  placeholder="Заглавие за търсачки"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">SEO Описание</label>
                <textarea
                  value={formData.seo_description}
                  onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })}
                  placeholder="Кратко описание за търсачки"
                  rows={3}
                  className="w-full px-3 py-2 bg-primary-black border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Извадка</label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  placeholder="Кратко резюме на страницата"
                  rows={2}
                  className="w-full px-3 py-2 bg-primary-black border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-neutral-700">
              <Button
                variant="outline"
                onClick={() => setShowEditor(false)}
                disabled={saving}
                className="flex-1"
              >
                <X className="w-4 h-4 mr-2" />
                Откажи
              </Button>
              <Button
                onClick={() => handleSave('draft')}
                disabled={saving}
                variant="outline"
                className="flex-1"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Запази като чернова
              </Button>
              <Button
                onClick={() => handleSave('published')}
                disabled={saving}
                className="flex-1 bg-accent-gold hover:bg-accent-gold-dark"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                Публикувай
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
